import { FunctionComponent, useEffect, useState } from "react";
import BasicModal from "@components/core/modals/BasicModal";
import { useTranslation } from "react-i18next";
import { OfficeModels } from "@type/core/models/models.types";
import { Select, Spin } from "antd";
import {
  requestGetModelTransaction,
  requestGetModelTransactionForOffices,
} from "@/state/transactionModels/TransactionModelEffects";
import { TransactionType } from "@/type/core/transaction/transaction.types";
import useOfficesToDisplay from "@/utils/hooks/useOfficesToDisplay";
import { toastError } from "@/utils/helpers/toast-helper";
import { setCurrentTransactionFromModel } from "@/state/transactions/CurrentTransactionEvents";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

type ModelOptions = {
  id: string;
  name: string;
  isSubtitle: boolean;
};

interface ChooseModelModalProps {
  visible: boolean;
  onCancel: () => void;
  onOk?: () => void;
  className?: string;
  type: TransactionType;
  url: string;
}

const ChooseModelModal: FunctionComponent<ChooseModelModalProps> = ({
  visible,
  onCancel,
  onOk,
  className = "",
  type,
  url,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { officesToDisplay } = useOfficesToDisplay();

  const [options, setOptions] = useState<ModelOptions[]>([]);
  const [modelTransactionId, setModelTransactionId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [officeModelsList, setOfficeModelsList] = useState<OfficeModels[]>([]);

  const showSelect = options.length > 0;

  useEffect(() => {
    const tmpOptions: ModelOptions[] = [];
    officeModelsList.forEach((officeModels) => {
      if (officeModels.models.length) {
        tmpOptions.push({
          id: officeModels.id,
          name: officeModels.name,
          isSubtitle: true,
        });
        officeModels.models.forEach((model) => {
          tmpOptions.push({
            id: model.id,
            name: model.name,
            isSubtitle: false,
          });
        });
      }
    });
    setOptions(tmpOptions);
    setModelTransactionId(
      (tmpOptions.length > 0 &&
        tmpOptions.find((option) => !option.isSubtitle)?.id) ||
        "",
    );
  }, [officeModelsList]);

  useEffect(() => {
    if (visible) {
      setIsLoading(true);
      void requestGetModelTransactionForOffices({
        dto: { type },
        id: officesToDisplay.map((office) => office.id).join(","),
      });
    }
  }, [visible, type, officesToDisplay]);

  useEffect(() => {
    return requestGetModelTransactionForOffices.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setOfficeModelsList(result.data);
      } else {
        toastError(t("error.toast.model.transactions"));
      }
      setIsLoading(false);
    });
  });

  const handleSubmit = () => {
    requestGetModelTransaction({ id: modelTransactionId })
      .then((response) => {
        if (response.ok && response.data) {
          setCurrentTransactionFromModel(response.data);
          navigate(url);
        } else {
          toastError(t("error.toast.model.transactions"));
        }
      })
      .catch(() => {
        toastError(t("error.toast.model.transactions"));
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <BasicModal
      visible={visible}
      onCancel={onCancel}
      onOk={() => {
        setIsLoading(true);
        onOk && onOk();
        handleSubmit();
        onCancel();
      }}
      buttonText={t("buttons.validate")}
      title={t("modals.chooseModel.title")}
      closable={false}
      className={`app-modal-choose-model ${className}`}
    >
      <>
        {isLoading ? (
          <div
            className="h-100 flex-grow-1 d-flex align-items-center justify-content-center"
            data-test="input-label"
            data-alt={t("modals.chooseModel.title")}
            data-test-for="modelSelection"
          >
            <Spin />
          </div>
        ) : showSelect ? (
          <Select
            className="w-100"
            data-test="modelSelection"
            value={modelTransactionId}
            onChange={(value: string) => setModelTransactionId(value)}
          >
            {options.map((option) => {
              if (option.isSubtitle) {
                return (
                  <Option
                    key={"office_" + option.id}
                    className="font-weight-bold"
                    data-test="select-list-option"
                    disabled
                  >
                    {option.name}
                  </Option>
                );
              } else {
                return (
                  <Option
                    key={"model_" + option.id}
                    value={option.id}
                    data-test="select-list-option"
                  >
                    &nbsp;&nbsp;{option.name}
                  </Option>
                );
              }
            })}
          </Select>
        ) : (
          <div>
            <p className="text-center">{t("modals.chooseModel.noModel")}</p>
          </div>
        )}
      </>
    </BasicModal>
  );
};

export default ChooseModelModal;
